<template>
  <section class="photo-upload" v-if="email && token">

    <main v-if="finished" class="photo-upload__done">
      <span class="photo-upload__done__icon">✓</span>
      <p class="photo-upload__done__title">¡Listo!</p>
      <p class="photo-upload__done__message">
        {{ feedback ? feedback.message : "Tu foto fue guardada correctamente" }}
      </p>
    </main>

    <main v-else-if="blocked" class="photo-upload__blocked">
      <p class="photo-upload__blocked__message">
        {{
          feedback
            ? feedback.message
            : "No es posible cargar la foto en este momento"
        }}
      </p>
    </main>

    <main v-else>
      <form class="photo-upload__form" autocomplete="off">
        <label class="photo-upload__label"
          ><strong>Tomar foto selfie</strong></label
        >

        <div :class="['photo-upload__file']">
          <div
            v-if="!photo"
            class="photo-upload__placeholder"
            @click="showCamera = true"
          >
            <selfie-icon color="#757575"></selfie-icon>
            <p class="photo-upload__hint">
              Click Aquí <touch-icon color="#757575"></touch-icon>
            </p>
          </div>
          <div v-else class="photo-upload__preview">
            <img class="photo-upload__image" :src="photo" />
            <span class="photo-upload__delete" @click="removePhoto">
              <delete-icon
                :width="'25px'"
                :height="'25px'"
                :color="'#ffa1a1'"
              />
            </span>
          </div>
        </div>

        <button
          v-if="photo"
          type="button"
          class="photo-upload__submit"
          :disabled="isLoading"
          @click="submitForm"
        >
          <span v-if="isLoading">Cargando...</span>
          <span v-else>{{ isRetry ? "Reintentar" : "Enviar" }}</span>
        </button>
      </form>

      <transition name="fade">
        <div
          v-if="feedback"
          :class="[
            'photo-upload__feedback',
            `photo-upload__feedback--${feedback.type}`
          ]"
        >
          {{ feedback.message }}
        </div>
      </transition>
    </main>

    <transition name="fade">
      <camera-capture
        :facial="false"
        class="photo-upload__camera"
        v-if="showCamera"
        @captured="onCaptured"
        @close="showCamera = false"
      />
    </transition>
  </section>
  <section v-else>
    No logramos obtener tus datos
  </section>
</template>

<script lang="js">
import SelfieIcon from '../../shared/icons/SelfieIcon.vue';
import TouchIcon from '../../shared/icons/TouchIcon.vue';
import DeleteIcon from '../../shared/icons/DeleteIcon.vue';
import CameraCapture from '../../shared/CameraCapture.vue';
import compressImage from '../../../utils/image-compresor';

const RESULT_FEEDBACK = {
  accepted:       { type: 'success' },
  saved_pending:  { type: 'success' },
  photo_error:    { type: 'error',   retake: true },
  device_offline: { type: 'warning', retry: true },
  invalid_photo:  { type: 'error',   retake: true },
  invalid_link:   { type: 'error',   blocked: true },
  invalid_user:   { type: 'error',   blocked: true },
}

const FALLBACK_MESSAGES = {
  accepted:       'Foto guardada y registrada correctamente',
  saved_pending:  'Tu foto fue guardada correctamente',
  photo_error:    'La foto fue rechazada por los dispositivos de acceso. Tómala nuevamente.',
  device_offline: 'Los dispositivos de acceso no están disponibles. Inténtalo nuevamente en unos minutos.',
  invalid_photo:  'La foto no es válida o supera el tamaño permitido (JPG/PNG, máximo 2MB). Tómala nuevamente.',
  invalid_link:   'Link inválido o expirado. Solicita un nuevo link.',
  invalid_user:   'No es posible cargar la foto en este momento.',
}

export default {
  name: 'PhotoUploadForm',
  components: {
    SelfieIcon,
    TouchIcon,
    DeleteIcon,
    CameraCapture,
  },
  data() {
    return {
      photo: null,
      email: null,
      token: null,
      companyId: null,
      rut: null,
      isLoading: false,
      showCamera: false,
      validated: false,
      feedback: null,
      finished: false,
      blocked: false,
      isRetry: false,
    }
  },
  created() {
    const { email, verify_code, company_id, rut } = this.$route.query
    this.email = email
    this.token = verify_code
    this.companyId = company_id
    this.rut = rut
  },
  methods: {
    async submitForm() {
      if (this.isLoading || this.blocked || this.finished) return
      this.isLoading = true
      this.feedback = null
      try {
        const response = await this.$http.post('users/photos', {
          photo: this.photo,
          email: this.email,
          token: this.token,
          companyId: this.companyId,
          rut: this.rut,
        })
        this.handlePhotoResponse(response)
      } catch (error) {
        if (error && error.status && error.body) {
          this.handlePhotoResponse(error)
        } else {
          this.feedback = {
            type: 'error',
            message: 'No pudimos conectar con el servidor. Revisa tu conexión e inténtalo nuevamente.',
          }
        }
      } finally {
        this.isLoading = false
      }
    },
    handlePhotoResponse(response) {
      const status = response.status
      const body = response.body || {}
      const resultConfig = RESULT_FEEDBACK[body.result]

      if (resultConfig && resultConfig.type === 'success') {
        this.finished = true
        this.feedback = { type: 'success', message: body.ans || FALLBACK_MESSAGES[body.result] }
        return
      }

      if (status >= 400 && (status === 401 || (resultConfig && resultConfig.blocked))) {
        this.blocked = true
        this.feedback = { type: 'error', message: body.ans || FALLBACK_MESSAGES[body.result || 'invalid_link'] }
        return
      }

      if (resultConfig) {
        this.feedback = { type: resultConfig.type, message: body.ans || FALLBACK_MESSAGES[body.result] }
        if (resultConfig.retake) this.removePhoto()
        this.isRetry = !!resultConfig.retry
        return
      }

      this.feedback = {
        type: 'error',
        message: 'Ocurrió un error al registrar la foto. Inténtalo nuevamente.',
      }
    },
    async onCaptured(photoData) {
      const compressedFile = await compressImage(photoData.target.files[0])

      const fileReader = new FileReader()

      fileReader.readAsDataURL(compressedFile)

      fileReader.onload = () => {
        this.photo = fileReader.result
        this.showCamera = false
      }
    },
    removePhoto() {
      this.photo = null
    },
  },
}
</script>

<style scoped lang="scss">
.photo-upload {
  &__title {
    font-size: 18px;
    text-align: center;
    color: black;
  }

  &__form {
    display: flex;
    flex-direction: column;
    margin-top: 2rem;
  }

  &__label {
    margin-bottom: 2rem;
    align-self: center;
    font-size: 15px;

    strong {
      color: #000;
    }
  }

  &__file {
    width: 230px;
    height: 230px;
    margin: 0 auto;
    cursor: pointer;
    position: relative;
    background: white;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.08);

    &--error {
      border: 1px solid #ff5252;
    }
  }

  &__placeholder {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    padding: 1rem 0;
  }

  &__hint {
    font-size: 18px;
    text-align: center;
  }

  &__preview {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  &__image {
    max-width: 240px;
    max-height: 240px;
    height: 98%;
  }

  &__delete {
    position: absolute;
    top: 1rem;
    right: 0.5rem;
    width: 26px;
    height: 26px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    background-color: white;
    cursor: pointer;
  }

  &__submit {
    align-self: center;
    margin-top: 2rem;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 5px;
    background-color: #007bff;
    color: white;
    cursor: pointer;
  }

  &__feedback {
    margin-top: 1.25rem;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    font-size: 14px;
    text-align: center;

    &--success {
      background: #e8f5e9;
      color: #2e7d32;
      border: 1px solid #a5d6a7;
    }
    &--error {
      background: #fdecea;
      color: #c62828;
      border: 1px solid #f5c6cb;
    }
    &--warning {
      background: #fff8e1;
      color: #a15c00;
      border: 1px solid #ffe08a;
    }
  }

  &__done {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 2rem;

    &__icon {
      font-size: 48px;
      color: #2e7d32;
    }
    &__title {
      font-size: 18px;
      font-weight: bold;
      margin-top: 0.5rem;
    }
    &__message {
      font-size: 14px;
      color: #757575;
      margin-top: 0.5rem;
      text-align: center;
    }
  }

  &__blocked {
    margin-top: 2rem;
    text-align: center;

    &__message {
      font-size: 14px;
      color: #c62828;
    }
  }
}
</style>
